import { useState, useMemo, useEffect, useCallback } from "react";
import { usePullToRefresh } from '../../utils/pullToRefresh/usePullToRefresh';
import { PullToRefreshIndicator } from '../../utils/pullToRefresh/PullToRefreshIndicator';
import { useCustomerSummaryInsights, useCustomerDetailedInsights, getCustomerDateRange } from "../../hooks/useCustomerInsights";
import { useNavigate } from "react-router-dom";
import { useBranch } from "../../context/BranchContext";
import { useAuth } from "../../context/AuthContext";
import { fetchTopCustomers } from "../../hooks/useCheckInReports";
import UserProfileDropdown, { getInitials } from "../layout/UserProfileDropdown";
import { useDesktopTopBar } from "../../context/DesktopLayoutContext";
import DesktopLayout from "../layout/DesktopLayout";
import TopBar, { DATE_PRESETS_DESKTOP_NO_TODAY, DATE_PRESETS_MOBILE_NO_TODAY } from "../layout/TopBar";
import BottomNav from "../BottomNav";
import { useSharedReportDateFilter } from "../../redux/reportFilters/reportFiltersHooks";
import "./reports.css";
import { T, useIsDesktop, Bone } from "./reportTokens";
import SectionError from "./SectionError";
import SectionEmpty from "./SectionEmpty";
import { useSwipeBack } from '../../hooks/useSwipeBack';
import "./CustomersReport.scss";

/* ═══════════════════════════════════════════════════════════════════════════
   MAGHIL ANALYTICS — CUSTOMERS REPORT
   Responsive: Desktop (≥900px) + Mobile (<900px) — single component, no duplication
   ═══════════════════════════════════════════════════════════════════════════ */

function CustomersDesktopShimmer() {
    const kpiLabels = ["NEW CUSTOMERS", "RETURNING"];
    return (
        <div className="cr-desktop-shimmer">
            {/* KPI Row */}
            <div className="cr-desktop-shimmer__kpi-row">
                {kpiLabels.map((label) => (
                    <div key={label} className="rpt-tile rpt-card cr-desktop-shimmer__kpi-tile">
                        <div className="rpt-tile__label cr-desktop-shimmer__kpi-label">{label}</div>
                        <Bone h={32} r={6} mb={6} />
                        <Bone h={12} w={70} r={4} />
                    </div>
                ))}
            </div>
            {/* Segments + Breakdown row */}
            <div className="cr-desktop-shimmer__two-col">
                <div className="rpt-card rpt-card--r16 cr-desktop-shimmer__segments">
                    <div className="rpt-section-title">Customer Segments (RFM)</div>
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="cr-desktop-shimmer__seg-row">
                            <div className="cr-desktop-shimmer__seg-row-header">
                                <Bone w={120} h={12} r={4} />
                                <Bone w={50} h={12} r={4} />
                            </div>
                            <Bone h={8} r={4} />
                        </div>
                    ))}
                </div>
                <div className="rpt-card rpt-card--r16 cr-desktop-shimmer__breakdown">
                    <div className="rpt-section-title">Segment Breakdown</div>
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="rpt-flex-row rpt-gap-12 cr-desktop-shimmer__bd-row">
                            <Bone w={100} h={12} r={4} />
                            <Bone w={60} h={12} r={4} />
                            <Bone w={40} h={12} r={4} />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function CustomersMobileShimmer() {
    const kpiLabels = ["NEW CUSTOMERS", "RETURNING"];
    return (
        <div className="cr-mobile-shimmer">
            <div className="cr-mobile-shimmer__kpi-grid">
                {kpiLabels.map((label) => (
                    <div key={label} className="cr-mobile-shimmer__kpi-tile">
                        <div className="cr-mobile-shimmer__kpi-label">{label}</div>
                        <Bone h={20} r={5} />
                    </div>
                ))}
            </div>
            <div className="cr-mobile-shimmer__seg-card">
                <div className="cr-mobile-shimmer__seg-title">Customer Segments</div>
                {[1, 2, 3].map((i) => (
                    <div key={i} className="cr-mobile-shimmer__seg-row">
                        <div className="cr-mobile-shimmer__seg-row-header">
                            <Bone w={90} h={11} r={4} />
                            <Bone w={35} h={11} r={4} />
                        </div>
                        <Bone h={6} r={3} />
                    </div>
                ))}
            </div>
        </div>
    );
}

const CHANNEL_COLORS = [T.accent, T.green, T.orange, T.blueDark, T.purple, T.teal];

const CUSTOMER_TABS = [
    { id: "dashboard", label: "Customers" },
    { id: "detailed", label: "Detailed Insights" },
];

const getCohortColor = (val: any) => {
    if (val === null || val === undefined) return T.hover;
    if (val >= 30) return T.green;
    if (val >= 20) return "rgba(22,163,74,0.55)";
    if (val >= 15) return "rgba(22,163,74,0.35)";
    if (val >= 10) return "rgba(22,163,74,0.2)";
    return "rgba(22,163,74,0.08)";
};

/* ═══════════════════════════════════════════════════════════════════════════
   SHARED RESPONSIVE COMPONENTS
   ═══════════════════════════════════════════════════════════════════════════ */
function Icon({ name, size = 20, color, fill = 0, weight = 400, style = {} }: any) {
    return (
        <span
            className="material-symbols-rounded cr-icon"
            style={{
                fontSize: size,
                color,
                fontVariationSettings: `'FILL' ${fill}, 'wght' ${weight}, 'opsz' ${size}`,
                ...style,
            }}
        >
            {name}
        </span>
    );
}

function SectionCard({ children, title, subtitle, icon, flex = 1, style = {}, rightAction, drillable, onTap }: any) {
    const isDesktop = useIsDesktop();
    return (
        <div
            onClick={!isDesktop && drillable ? onTap : undefined}
            className={`cr-section-card${drillable && !isDesktop ? " cr-section-card--drillable" : ""}`}
            style={{ flex, ...style }}
        >
            {!isDesktop && drillable && <span className="cr-section-card__chevron"><Icon name="chevron_right" size={14} color={T.accent} /></span>}
            {title && (
                <div className="cr-section-card__header">
                    <div className="cr-section-card__header-left">
                        {icon && <Icon name={icon} size={isDesktop ? 18 : 15} color={T.t3} weight={400} />}
                        <div>
                            <div className="cr-section-card__title">{title}</div>
                            {subtitle && <div className="cr-section-card__subtitle">{subtitle}</div>}
                        </div>
                    </div>
                    {isDesktop && rightAction}
                </div>
            )}
            {children}
        </div>
    );
}

// FilterPill removed with the local date-preset row (GLOBAL-DP-6) — it had no other caller.

function SegmentBadge({ label, color }: any) {
    return (
        <span
            className="cr-segment-badge"
            style={{ color, background: `${color}14` }}
        >
            <span className="cr-segment-badge__dot" style={{ background: color }} />
            {label}
        </span>
    );
}

function ChurnBanner({ variant = "desktop" }: any) {
    const isMobile = variant === "mobile";
    return (
        <div className="cr-churn-banner">
            <span className="cr-churn-banner__icon"><Icon name="warning" size={isMobile ? 16 : 22} color={T.orange} fill={1} /></span>
            <div className="cr-churn-banner__body">
                <div className="cr-churn-banner__title">Churn insights</div>
                <div className="cr-churn-banner__desc">
                    At-risk and churn alerts will appear here when data is available.
                </div>
            </div>
            {isMobile ? (
                <span className="cr-churn-banner__chevron"><Icon name="chevron_right" size={14} color={T.orange} /></span>
            ) : (
                <button className="cr-churn-banner__btn">
                    View At-Risk List
                </button>
            )}
        </div>
    );
}

function SegmentTile({ seg, isDesktop, maxCount }: any) {
    // Size proportional to this segment's share of the largest segment, so the
    // biggest/medium/smallest tiers are visually distinct (sqrt dampens the spread
    // so tiny segments stay readable).
    const ratio = maxCount > 0 ? Math.min(1, seg.count / maxCount) : 0;
    const scaled = Math.sqrt(ratio);
    const size = isDesktop
        ? Math.round(80 + scaled * 100)   // desktop: 80 → 180
        : Math.round(52 + scaled * 72);   // mobile: 52 → 124
    return (
        <div
            className="cr-segment-tile"
            style={{
                width: size,
                height: size,
                background: isDesktop ? `${seg.color}18` : `${seg.color}14`,
                border: `1.5px solid ${isDesktop ? `${seg.color}40` : `${seg.color}30`}`,
            }}
        >
            <div className="cr-segment-tile__count" style={{ color: seg.color }}>{seg.count}</div>
            <div className="cr-segment-tile__name" style={{ color: seg.color }}>
                {seg.name}
            </div>
            {isDesktop && <div className="cr-segment-tile__pct">{seg.pct}%</div>}
        </div>
    );
}

function TopCustomerRow({ c, index, segmentRows, variant = "table" }: any) {
    const name = c.customerName || c.name || "—";
    const initials = name.split(" ").map((n) => n[0]).join("").slice(0, 2) || "—";
    const seg = segmentRows.find((s) => s.name === (c.segment || c.loyaltyCategory));
    const segmentLabel = c.segment || c.loyaltyCategory || "—";
    const segmentColor = seg?.color || T.t3;
    const visits = c.visits ?? c.totalVisits ?? "—";
    const lastVisit = c.lastVisit ?? c.latestVisit ?? "—";
    const channel = c.channel || c.primaryChannel || "—";
    const spend = c.totalSpend ?? c.spend ?? "—";
    const colorIdx = index % CHANNEL_COLORS.length;

    if (variant === "card") {
        return (
            <div className="cr-top-customer-card">
                <div className="cr-top-customer-card__header">
                    <span className={`cr-top-customer-card__rank${index === 0 ? " cr-top-customer-card__rank--first" : ""}`}>{index + 1}</span>
                    <div className="cr-top-customer-card__avatar" style={{ background: `${CHANNEL_COLORS[colorIdx]}18`, color: CHANNEL_COLORS[colorIdx] }}>{initials}</div>
                    <div className="cr-top-customer-card__name-wrap"><div className="cr-top-customer-card__name">{name}</div></div>
                    <div className="cr-top-customer-card__spend">{spend}</div>
                </div>
                <div className="cr-top-customer-card__tags">
                    <SegmentBadge label={segmentLabel} color={segmentColor} />
                    <span className="cr-top-customer-card__tag"><Icon name="replay" size={9} color={T.t3} />{visits} visits</span>
                    <span className="cr-top-customer-card__tag"><Icon name="schedule" size={9} color={T.t3} />{lastVisit}</span>
                    <span className="cr-top-customer-card__tag"><Icon name="restaurant" size={9} color={T.t3} />{channel}</span>
                </div>
            </div>
        );
    }
    return (
        <tr className="cr-top-customer-tr">
            <td className="cr-top-customer-tr__td">
                <span className={`cr-top-customer-tr__rank${index === 0 ? " cr-top-customer-tr__rank--first" : ""}`}>{index + 1}</span>
            </td>
            <td className="cr-top-customer-tr__td">
                <div className="cr-top-customer-tr__name-cell">
                    <div className="cr-top-customer-tr__avatar" style={{ background: `${CHANNEL_COLORS[colorIdx]}18`, color: CHANNEL_COLORS[colorIdx] }}>{initials}</div>
                    <span className="cr-top-customer-tr__name">{name}</span>
                </div>
            </td>
            <td className="cr-top-customer-tr__td">{visits}</td>
            <td className="cr-top-customer-tr__td cr-top-customer-tr__td--spend">{spend}</td>
            <td className="cr-top-customer-tr__td cr-top-customer-tr__td--lastvisit">{lastVisit}</td>
            <td className="cr-top-customer-tr__td">
                <SegmentBadge label={segmentLabel} color={segmentColor} />
            </td>
            <td className="cr-top-customer-tr__td cr-top-customer-tr__td--channel">{channel}</td>
        </tr>
    );
}

function KPICard({ label, value, delta, positive, icon, sub }: any) {
    const isDesktop = useIsDesktop();
    const deltaColor = positive ? T.green : T.red;
    return (
        <div className="cr-kpi-card">
            <div className={`cr-kpi-card__glow${positive ? " cr-kpi-card__glow--positive" : " cr-kpi-card__glow--negative"}`} />
            {isDesktop ? (
                <>
                    <div className="cr-kpi-card__header">
                        <div className="cr-kpi-card__label">{label}</div>
                        {icon && <Icon name={icon} size={18} color={T.t3} weight={300} />}
                    </div>
                    <div className="cr-kpi-card__value">{value}</div>
                    {delta && delta !== "—" && (
                        <div className="cr-kpi-card__delta" style={{ color: deltaColor }}>
                            <Icon name={positive ? "trending_up" : "trending_down"} size={13} color={deltaColor} />
                            {delta}
                        </div>
                    )}
                    {sub && <div className="cr-kpi-card__sub">{sub}</div>}
                </>
            ) : (
                <>
                    <div className="cr-kpi-card__label">{label}</div>
                    <div className="cr-kpi-card__value">{value}</div>
                    {delta && delta !== "—" && (
                        <div className="cr-kpi-card__delta" style={{ color: deltaColor }}>
                            <Icon name={positive ? "trending_up" : "trending_down"} size={10} color={deltaColor} />
                            {delta}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function hexToRgba(hex: string, alpha: number): string {
    const clean = hex.replace('#', '');
    const full = clean.length === 3 ? clean.split('').map(c => c + c).join('') : clean;
    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${alpha})`;
}

function SimpleBarChart({ data, labelKey, valueKey, color = T.accent, height = 32 }: any) {
    const maxV = data.length ? Math.max(...data.map((d) => Number(d[valueKey]) || 0), 1) : 1;
    const n = data.length;
    const useRankedColors = n >= 8;
    const sortedByVal = useRankedColors
        ? [...data].map((d: any, i: number) => ({ i, v: Number(d[valueKey]) || 0 })).sort((a, b) => b.v - a.v)
        : [];
    const rank1Idx = sortedByVal[0]?.i ?? -1;
    const rank2Idx = sortedByVal[1]?.i ?? -1;
    const rank3Idx = sortedByVal[2]?.i ?? -1;
    const getFillBg = (i: number) => {
        if (!useRankedColors) return color;
        if (i === rank1Idx) return color;
        if (i === rank2Idx) return hexToRgba(color, 0.75);
        if (i === rank3Idx) return hexToRgba(color, 0.55);
        return hexToRgba(color, 0.40);
    };
    return (
        <div className="cr-bar-chart">
            {data.map((d, i) => {
                const v = Number(d[valueKey]) || 0;
                const pct = maxV ? (v / maxV) * 100 : 0;
                return (
                    <div key={i}>
                        <div className="cr-bar-chart__row-header">
                            <span className="cr-bar-chart__label">{d[labelKey]}</span>
                            <span className="cr-bar-chart__value">{v}</span>
                        </div>
                        <div className="cr-bar-chart__track" style={{ height }}>
                            <div className="cr-bar-chart__fill" style={{ width: `${pct}%`, background: getFillBg(i) }} />
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

/* Summary Insights — API-driven */
function SummaryInsightsContent({ locationId, volumeSummary, byTenure, byTotalSpend, byAvgCoverSize, loyaltyCounts, loading, error }: any) {
    const isDesktop = useIsDesktop();
    const categories = volumeSummary?.categories ?? [];
    const tenureFiltered = (byTenure || []).filter((r) => (r.customerCount || 0) > 0);
    const spendFiltered = (byTotalSpend || []).slice(0, 12);
    const coverFiltered = (byAvgCoverSize || []).slice(0, 10);
    const loyaltyFiltered = (loyaltyCounts || []).slice(0, 10);
    if (!locationId) return <div className="cr-message">Select a location to view Customer Insights.</div>;
    if (loading) return isDesktop ? <CustomersDesktopShimmer /> : <CustomersMobileShimmer />;
    if (error) return <div className="cr-message cr-message--error">{error}</div>;
    if (!categories.length && !tenureFiltered.length && !spendFiltered.length && !coverFiltered.length && !loyaltyFiltered.length) {
        return <div className="cr-message">No data for this location.</div>;
    }
    return (
        <div className="cr-summary">
            <div className="cr-summary__row">
                <SectionCard title="Customer Volume Summary" subtitle="By order category" icon="bar_chart" flex={1} style={{ minWidth: isDesktop ? 280 : "100%" }}>
                    {categories.length ? <SimpleBarChart data={categories} labelKey="orderCategory" valueKey="customerCount" color={T.accent} /> : <SectionEmpty kpiName="customer volume" minHeight={140} />}
                </SectionCard>
                <SectionCard title="Customers By Loyalty" icon="loyalty" flex={1} style={{ minWidth: isDesktop ? 280 : "100%" }}>
                    {loyaltyFiltered.length ? <SimpleBarChart data={loyaltyFiltered} labelKey="loyaltyCategory" valueKey="customerCount" color={T.green} /> : <SectionEmpty kpiName="customers by loyalty" minHeight={140} />}
                </SectionCard>
            </div>
            <SectionCard title="Customers By Tenure" subtitle="Active = order in last 3 months" icon="schedule">
                {tenureFiltered.length ? (
                    <div className="cr-table__overflow">
                        <table className="cr-table">
                            <thead>
                                <tr>
                                    <th>TIMELINE</th>
                                    <th>STATE</th>
                                    <th className="cr-table-th--right">COUNT</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tenureFiltered.map((r, i) => (
                                    <tr key={i}>
                                        <td>{r.timeline}</td>
                                        <td className="cr-table-td--t2">{r.customerState}</td>
                                        <td className="cr-table-td--right cr-table-td--bold">{r.customerCount}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <SectionEmpty kpiName="customers by tenure" minHeight={140} />
                )}
            </SectionCard>
            <div className="cr-summary__row">
                <SectionCard title="Customers By Total Spend" icon="payments" flex={1} style={{ minWidth: isDesktop ? 320 : "100%" }}>
                    {spendFiltered.length ? (
                        <table className="cr-table">
                            <thead>
                                <tr>
                                    <th className="cr-table-th--sm">SPEND</th>
                                    <th className="cr-table-th--sm">ORDER TYPE</th>
                                    <th className="cr-table-th--sm cr-table-th--right">COUNT</th>
                                </tr>
                            </thead>
                            <tbody>
                                {spendFiltered.map((r, i) => (
                                    <tr key={i}>
                                        <td className="cr-table-td--sm-pad cr-table-td--sm">{r.spendCategory}</td>
                                        <td className="cr-table-td--sm-pad cr-table-td--sm cr-table-td--t2">{r.orderCategory}</td>
                                        <td className="cr-table-td--sm-pad cr-table-td--sm cr-table-td--bold cr-table-td--right">{r.customerCount}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <SectionEmpty kpiName="customers by total spend" minHeight={140} />
                    )}
                </SectionCard>
                <SectionCard title="Customers By Avg Cover Size" icon="group" flex={1} style={{ minWidth: isDesktop ? 280 : "100%" }}>
                    {coverFiltered.length ? <SimpleBarChart data={coverFiltered.map((d) => ({ ...d, orderTotalRange: d.orderTotalRange ?? d.avgCoverRange ?? d.coverSizeRange ?? d.label ?? "—" }))} labelKey="orderTotalRange" valueKey="customerCount" color={T.blueDark} /> : <SectionEmpty kpiName="customers by avg cover size" minHeight={140} />}
                </SectionCard>
            </div>
        </div>
    );
}

/* Customer-d format dashboard: KPI row, Segments (loyalty), Cohort, Top Customers, Churn */
function CustomersDashboardContent({
    locationId,
    volumeSummary,
    byTenure,
    byTotalSpend,
    byAvgCoverSize,
    loyaltyCounts,
    loading,
    error,
    sectionErrors,
    retrySection,
    datePeriod,
    setDatePeriod,
    datePresets,
    topCustomers = [],
    topCustomersLoading,
    topCustomerSearch,
    setTopCustomerSearch,
}: any) {
    const isDesktop = useIsDesktop();
    const tenureRows = Array.isArray(byTenure) ? byTenure : [];
    const totalFromLoyalty = (loyaltyCounts || []).reduce((s, r) => s + (Number(r.customerCount) || 0), 0);
    const categories = volumeSummary?.categories ?? [];
    const totalFromVolume = categories.reduce((s, c) => s + (Number(c.customerCount) || 0), 0);
    const totalCustomers = totalFromLoyalty || totalFromVolume || 0;

    const normalizeTimeline = (timeline: any) => String(timeline || "").trim().toLowerCase().replace(/\s+/g, " ");
    const normalizeState = (state: any) => String(state || "").trim().toLowerCase();
    const isThirtyDayTimeline = (timeline: any) => {
        const t = normalizeTimeline(timeline);
        return t === "1 month" || t === "30 days" || t === "30 day" || t === "30d";
    };

    const newCustomersCount = tenureRows.reduce((sum, row) => {
        const t = normalizeTimeline(row?.timeline);
        const isActive = normalizeState(row?.customerState) === "active";
        return (t === "1 month") && isActive ? sum + (Number(row?.customerCount) || 0) : sum;
    }, 0);

    const returningCustomersCount = tenureRows.reduce((sum, row) => {
        const t = normalizeTimeline(row?.timeline);
        const isActive = normalizeState(row?.customerState) === "active";
        return (t === "6 months") && isActive ? sum + (Number(row?.customerCount) || 0) : sum;
    }, 0);

    const thirtyDayCustomersCount = tenureRows.reduce((sum, row) => {
        return isThirtyDayTimeline(row?.timeline) ? sum + (Number(row?.customerCount) || 0) : sum;
    }, 0);

    const segmentRows = useMemo(() => {
        return (loyaltyCounts || [])
            .map((r, i) => {
                const count = Number(r.customerCount) || 0;
                const pct = totalCustomers ? ((count / totalCustomers) * 100).toFixed(1) : "0";
                return {
                    name: r.loyaltyCategory || r.segment || `Segment ${i + 1}`,
                    count,
                    pct: parseFloat(pct),
                    color: CHANNEL_COLORS[i % CHANNEL_COLORS.length],
                };
            })
            .filter((s) => s.count > 0);
    }, [loyaltyCounts, totalCustomers]);

    const kpis = useMemo(() => {
        const base = [
            { label: "NEW CUSTOMERS", value: newCustomersCount.toLocaleString(), delta: "—", positive: true, icon: "person_add", sub: "Active in first 1 month" },
            { label: "RETURNING", value: returningCustomersCount.toLocaleString(), delta: "—", positive: true, icon: "loyalty", sub: "Active within 6 months" },
            // { label: "30-DAY CUSTOMERS", value: thirtyDayCustomersCount.toLocaleString(), delta: "—", positive: true, icon: "replay_30", sub: "From tenure insights" },
            /* Churn Rate, Avg CLV, At Risk — commented out
            { label: "CHURN RATE", value: "—", delta: "—", positive: true, icon: "person_off", sub: "60-day threshold" },
            { label: "AVG CLV", value: "—", delta: "—", positive: true, icon: "diamond", sub: "Lifetime value" },
            { label: "AT RISK", value: "—", delta: "—", positive: false, icon: "warning", sub: "Win-back priority" },
            */
        ];
        return base;
    }, [newCustomersCount, returningCustomersCount, thirtyDayCustomersCount]);

    const filteredTop = useMemo(
        () =>
            (topCustomers || []).filter((c) => !topCustomerSearch || String(c.customerName || c.name || "").toLowerCase().includes(topCustomerSearch.toLowerCase())),
        [topCustomers, topCustomerSearch]
    );

    if (!locationId) {
        return <div className="cr-message">Select a location to view the Customers dashboard.</div>;
    }
    if (loading) return isDesktop ? <CustomersDesktopShimmer /> : <CustomersMobileShimmer />;
    if (error) return <div className="cr-message cr-message--error">{error}</div>;

    /* Mobile layout (customerm.jsx style): KPI 2×3, churn, segments, cohort, top customers as cards */
    if (!isDesktop) {
        const kpiRow1 = kpis.slice(0, 3);
        const kpiRow2 = kpis.slice(3, 6);
        return (
            <div className="cr-dashboard-mobile">
                <div className="cr-dashboard-mobile__kpi-grid" style={{ gridTemplateColumns: `repeat(${Math.max(1, kpiRow1.length)}, 1fr)` }}>
                    {kpiRow1.map((kpi, i) => (
                        <KPICard key={i} label={kpi.label} value={kpi.value} delta={kpi.delta} positive={kpi.positive} icon={kpi.icon} sub={kpi.sub} />
                    ))}
                </div>
                {kpiRow2.length > 0 && (
                <div className="cr-dashboard-mobile__kpi-grid cr-dashboard-mobile__kpi-grid--second" style={{ gridTemplateColumns: `repeat(${Math.max(1, kpiRow2.length)}, 1fr)` }}>
                    {kpiRow2.map((kpi, i) => (
                        <KPICard key={i} label={kpi.label} value={kpi.value} delta={kpi.delta} positive={kpi.positive} icon={kpi.icon} sub={kpi.sub} />
                    ))}
                </div>
                )}
                {/* Churn insights — commented out
                <ChurnBanner variant="mobile" />
                */}
                <SectionCard title="Customer Segments" subtitle={totalCustomers ? `${totalCustomers.toLocaleString()} identified` : "By loyalty"} icon="pie_chart" style={{ marginBottom: 8 }}>
                    {sectionErrors?.loyalty ? (
                        <SectionError kpiName="customer segments" onRetry={() => retrySection?.('loyalty')} minHeight={160} />
                    ) : segmentRows.length ? (
                        <>
                            {segmentRows.map((seg, i) => (
                                <div key={i} className="cr-dashboard-mobile__seg-list-row">
                                    <span className="cr-dashboard-mobile__seg-dot" style={{ background: seg.color }} />
                                    <span className="cr-dashboard-mobile__seg-name">{seg.name}</span>
                                    <span className="cr-dashboard-mobile__seg-count">{seg.count}</span>
                                    <span className="cr-dashboard-mobile__seg-pct">{seg.pct}%</span>
                                </div>
                            ))}
                        </>
                    ) : (
                        <SectionEmpty kpiName="customer segments" minHeight={160} />
                    )}
                </SectionCard>
                {/* Cohort Retention — commented out
                <SectionCard title="Cohort Retention" subtitle="Monthly return rates" icon="grid_on" style={{ marginBottom: 8 }}>
                    <div className="cr-message--sm">No cohort data available yet.</div>
                </SectionCard>
                */}
                {/* Top Customers — commented out
                <SectionCard title="Top Customers" subtitle="Highest lifetime spend" icon="workspace_premium" style={{ marginBottom: 8 }}>
                    <div className="cr-mobile-search">
                        <Icon name="search" size={14} color={T.t3} />
                        <input placeholder="Search..." value={topCustomerSearch || ""} onChange={(e) => setTopCustomerSearch && setTopCustomerSearch(e.target.value)} className="cr-mobile-search__input" />
                    </div>
                    {topCustomersLoading ? (
                        <div className="cr-message--sm">Loading…</div>
                    ) : !filteredTop.length ? (
                        <div className="cr-message--sm">No top customers for this period.</div>
                    ) : (
                        <div className="cr-col-stack">
                            {filteredTop.map((c, i) => (
                                <TopCustomerRow key={i} c={c} index={i} segmentRows={segmentRows} variant="card" />
                            ))}
                        </div>
                    )}
                </SectionCard>
                */}
                <div className="cr-dashboard-mobile__spacer" />
            </div>
        );
    }

    /* Desktop layout */
    return (
        <div className="cr-dashboard">
            {/* KPI row */}
            <div className="cr-dashboard__kpi-row">
                {kpis.map((kpi, i) => (
                    <KPICard key={i} label={kpi.label} value={kpi.value} delta={kpi.delta} positive={kpi.positive} icon={kpi.icon} sub={kpi.sub} />
                ))}
            </div>

            {/* Customer Segments (RFM-style from loyalty) */}
            <div className="cr-dashboard__segments-row">
                <SectionCard title="Customer Segments (RFM)" subtitle={totalCustomers ? `${totalCustomers.toLocaleString()} identified customers` : "By loyalty"} icon="pie_chart" flex={1.2} style={{ minWidth: isDesktop ? 320 : "100%" }}>
                    {sectionErrors?.loyalty ? (
                        <SectionError kpiName="customer segments" onRetry={() => retrySection?.('loyalty')} minHeight={200} />
                    ) : segmentRows.length ? (
                        <div className="cr-dashboard__segment-tiles">
                            {(() => {
                                const maxCount = segmentRows.reduce((m: number, s: any) => Math.max(m, Number(s.count) || 0), 0);
                                return segmentRows.map((seg, i) => (
                                    <SegmentTile key={i} seg={seg} isDesktop={true} maxCount={maxCount} />
                                ));
                            })()}
                        </div>
                    ) : (
                        <SectionEmpty kpiName="customer segments" minHeight={200} />
                    )}
                </SectionCard>
                <SectionCard title="Segment Breakdown" subtitle="Key metrics per segment" icon="table_chart" flex={1.4} style={{ minWidth: isDesktop ? 340 : "100%" }}>
                    {sectionErrors?.loyalty ? (
                        <SectionError kpiName="segment breakdown" onRetry={() => retrySection?.('loyalty')} minHeight={200} />
                    ) : segmentRows.length ? (
                        <div className="cr-table__overflow">
                            <table className="cr-table">
                                <thead>
                                    <tr>
                                        {["SEGMENT", "CUSTOMERS", "% OF TOTAL"].map((h) => (
                                            <th key={h}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {segmentRows.map((seg, i) => (
                                        <tr key={i}>
                                            <td>
                                                <SegmentBadge label={seg.name} color={seg.color} />
                                            </td>
                                            <td className="cr-table-td--fw500">{seg.count.toLocaleString()}</td>
                                            <td className="cr-table-td--t2">{seg.pct}%</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <SectionEmpty kpiName="segment breakdown" minHeight={200} />
                    )}
                </SectionCard>
            </div>

            {/* Cohort Retention — commented out
            <SectionCard title="Cohort Retention" subtitle="Monthly cohort return rates" icon="grid_on" style={{ marginBottom: 0 }}>
                <div className="cr-message">No cohort data available yet.</div>
            </SectionCard>
            */}

            {/* Top Customers — commented out
            <SectionCard
                title="Top Customers"
                subtitle="Highest lifetime spend"
                icon="workspace_premium"
                style={{ marginBottom: 0 }}
                rightAction={
                    <div className="cr-desktop-search">
                        <Icon name="search" size={15} color={T.t3} />
                        <input
                            placeholder="Search customers..."
                            value={topCustomerSearch || ""}
                            onChange={(e) => setTopCustomerSearch && setTopCustomerSearch(e.target.value)}
                            className="cr-desktop-search__input"
                        />
                    </div>
                }
            >
                {topCustomersLoading ? (
                    <div className="cr-message">Loading top customers…</div>
                ) : !filteredTop.length ? (
                    <div className="cr-message">No top customers data for this period.</div>
                ) : (
                    <div className="cr-table__overflow">
                        <table className="cr-table">
                            <thead>
                                <tr>
                                    {["#", "CUSTOMER", "VISITS", "TOTAL SPEND", "LAST VISIT", "SEGMENT", "PRIMARY CHANNEL"].map((h) => (
                                        <th key={h}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredTop.map((c, i) => (
                                    <TopCustomerRow key={i} c={c} index={i} segmentRows={segmentRows} variant="table" />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </SectionCard>
            */}

            {/* Churn insights — commented out
            <ChurnBanner variant="desktop" />
            */}
        </div>
    );
}

/* Detailed Insights — customer search + tables */
function DetailedInsightsContent({
    datePeriod, setDatePeriod, datePresets,
    searchQuery, setSearchQuery, customerOptions, searchLoading,
    selectedCustomer, setSelectedCustomer, summary, dineIn, offPrem, orderHistory, latestOrder, favouriteItems,
    detailLoading, detailError, clearCustomer,
    orderPage, setOrderPage,
    locationId,
}: any) {
    const hasCustomer = !!selectedCustomer?.value || !!selectedCustomer?.phoneNumber;
    const summaryRow = summary ? [summary] : [];
    const dineInRow = dineIn ? [dineIn] : [];
    const offPremRow = offPrem ? [offPrem] : [];
    const latestRow = latestOrder ? [latestOrder] : [];
    const renderTable = (title: any, rows: any, columns: any) => {
        if (!rows.length) return null;
        return (
            <SectionCard title={title} icon="table_chart" style={{ marginBottom: 16 }}>
                <div className="cr-table__overflow">
                    <table className="cr-table">
                        <thead>
                            <tr>
                                {columns.map((c) => (
                                    <th key={c.key}>{c.label}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, i) => (
                                <tr key={i}>
                                    {columns.map((c) => {
                                        const val = r[c.key] ?? (c.fallbackKey != null ? r[c.fallbackKey] : undefined);
                                        return <td key={c.key}>{val != null ? String(val) : "—"}</td>;
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </SectionCard>
        );
    };
    return (
        <div className="cr-detailed">
            <div className="cr-detailed__toolbar">
                {!locationId && <div className="cr-message--warn">Select a location to search customers.</div>}
                {/* GLOBAL-DP-6 (ZAR-1639): the Detailed tab's own preset pill row and native
                    <input type="date"> custom picker are removed. This report already drives
                    the SAME `datePeriod` state through the unified date control in the TopBar,
                    so the local row was a second, differently-behaved date control on one
                    screen — exactly what the epic exists to eliminate. */}
                <div className="cr-detailed__search-row">
                    <div className="cr-detailed__search-wrap">
                        <span className="cr-detailed__search-icon"><Icon name="search" size={16} color={T.t3} /></span>
                        <input
                            placeholder="Search customer..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="cr-detailed__search-input"
                        />
                        {searchLoading && <span className="cr-detailed__search-loading"><Bone w={50} h={10} r={4} /></span>}
                    </div>
                    {customerOptions.length > 0 && (
                        <select
                            value={selectedCustomer?.value ?? ""}
                            onChange={(e) => {
                                const v = e.target.value;
                                setSelectedCustomer(customerOptions.find((o) => o.value === v) || null);
                            }}
                            className="cr-detailed__customer-select"
                        >
                            <option value="">Select customer</option>
                            {customerOptions.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    )}
                    {hasCustomer && (
                        <button type="button" onClick={clearCustomer} className="cr-detailed__clear-btn">Clear</button>
                    )}
                </div>
            </div>
            {!hasCustomer && <div className="cr-message">Select date range and search for a customer to view detailed insights.</div>}
            {hasCustomer && detailError && <div className="cr-message--detail-error">{detailError}</div>}
            {hasCustomer && detailLoading && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '8px 0' }}>
                    {[8, 6, 6].map((cols, si) => (
                        <div key={si} className="rpt-card" style={{ borderRadius: 12 }}>
                            <Bone w={100} h={13} r={4} mb={14} />
                            {Array.from({ length: 3 }).map((_, i) => (
                                <div key={i} className="rpt-flex-row rpt-gap-12" style={{ padding: '9px 0', borderBottom: i < 2 ? `1px solid ${T.divider}` : 'none' }}>
                                    <Bone w={90} h={12} r={4} />
                                    {Array.from({ length: Math.min(cols - 1, 3) }).map((_, j) => (
                                        <Bone key={j} w={null} h={12} r={4} style={{ flex: 1 }} />
                                    ))}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            )}
            {hasCustomer && !detailLoading && (
                <>
                    {renderTable("Summary", summaryRow, [{ key: "fullName", label: "Name" }, { key: "phoneNumber", label: "Contact" }, { key: "email", label: "Email" }, { key: "loyaltyType", label: "Loyalty", fallbackKey: "loyaltyTyp" }, { key: "tenureMonths", label: "Tenure (mo)" }, { key: "totalVisits", label: "Visits" }, { key: "totalSpend", label: "Spent" }, { key: "canceledOrdersCount", label: "Cancelled" }])}
                    {renderTable("Dine-in", dineInRow, [{ key: "grpType", label: "Group type" }, { key: "firstVisit", label: "First visit" }, { key: "latestVisit", label: "Latest visit" }, { key: "dineinTenure", label: "Tenure" }, { key: "totalVisits", label: "Visits" }, { key: "avgGrpSize", label: "Avg group size" }])}
                    {renderTable("Off-prem", offPremRow, [{ key: "firstOrder", label: "First order" }, { key: "lastOrder", label: "Last order" }, { key: "totalOrders", label: "Orders" }, { key: "totalItems", label: "Items" }, { key: "maxOrderAmount", label: "Max amount" }, { key: "totalSpent", label: "Total spent" }, { key: "avgCoverSize", label: "Avg cover" }])}
                    {renderTable("Latest Order", latestRow, [{ key: "orderDate", label: "Date" }, { key: "itemDetails", label: "Items" }])}
                    {favouriteItems.length > 0 && (
                        <SectionCard title="Top 10 Favorite Items" icon="favorite" style={{ marginBottom: 16 }}>
                            <table className="cr-table">
                                <thead>
                                    <tr>
                                        <th>Item</th>
                                        <th className="cr-table-th--right">Qty</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {favouriteItems.map((r, i) => (
                                        <tr key={i}>
                                            <td>{r.favoriteItem ?? r.favouriteItem ?? "—"}</td>
                                            <td className="cr-table-td--right">{r.qtyOrdered ?? r.quantity ?? "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </SectionCard>
                    )}
                    {orderHistory.content && orderHistory.content.length > 0 && (
                        <SectionCard title="Order History" subtitle={`Page ${orderPage}`} icon="history" style={{ marginBottom: 16 }}>
                            <div className="cr-table__overflow">
                                <table className="cr-table">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Order no</th>
                                            <th>Type</th>
                                            <th>Items</th>
                                            <th>Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {orderHistory.content.map((r, i) => (
                                            <tr key={i}>
                                                <td>{r.orderDate}</td>
                                                <td>{r.orderNo}</td>
                                                <td>{r.orderType}</td>
                                                <td>{r.itemDetails}</td>
                                                <td>{r.orderTotal}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="cr-pagination">
                                <span className="cr-pagination__total">Total: {orderHistory.totalElements}</span>
                                <div className="cr-pagination__btns">
                                    <button type="button" disabled={orderPage <= 1} onClick={() => setOrderPage((p) => p - 1)} className="cr-pagination__btn">Prev</button>
                                    <button type="button" disabled={orderPage >= (orderHistory.totalPages || 1)} onClick={() => setOrderPage((p) => p + 1)} className="cr-pagination__btn">Next</button>
                                </div>
                            </div>
                        </SectionCard>
                    )}
                </>
            )}
        </div>
    );
}


function MobileLayout({ branchName, user, onBack, activeTab, setActiveTab, datePeriod, setDatePeriod, orderHistoryPage, setOrderHistoryPage, orderHistorySize, summaryApi, detailedApi, topCustomers, topCustomersLoading, topCustomerSearch, setTopCustomerSearch }: any) {
    const setTopBarProps = useDesktopTopBar();
    // Customers has no live-day data path, so it offers every preset except Today (excludeToday).
    const dateOptions = DATE_PRESETS_MOBILE_NO_TODAY;

    useEffect(() => {
        setTopBarProps({
            title: "Customers",
            dateFilters: { value: datePeriod, onChange: setDatePeriod, options: dateOptions, excludeToday: true },
        });
    }, [setTopBarProps, datePeriod, setDatePeriod]);

    const handlePtrRefresh = useCallback(() => {
        summaryApi.refetch?.();
    }, [summaryApi.refetch]);
    const { scrollRef: ptrScrollRef, pullDistance, isRefreshing: ptrRefreshing } = usePullToRefresh({ onRefresh: handlePtrRefresh });

    return (
        <div className="cr-mobile-layout">
            <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={ptrRefreshing} />
            <TopBar variant="mobile" />

            <div ref={ptrScrollRef} className="cr-mobile-layout__content">
                {activeTab === "dashboard" ? (
                    <CustomersDashboardContent
                        locationId={summaryApi.locationId}
                        volumeSummary={summaryApi.volumeSummary}
                        byTenure={summaryApi.byTenure}
                        byTotalSpend={summaryApi.byTotalSpend}
                        byAvgCoverSize={summaryApi.byAvgCoverSize}
                        loyaltyCounts={summaryApi.loyaltyCounts}
                        loading={summaryApi.loading}
                        error={summaryApi.error}
                        sectionErrors={summaryApi.sectionErrors}
                        retrySection={summaryApi.retrySection}
                        datePeriod={datePeriod}
                        setDatePeriod={setDatePeriod}
                        datePresets={dateOptions}
                        topCustomers={topCustomers || []}
                        topCustomersLoading={topCustomersLoading}
                        topCustomerSearch={topCustomerSearch}
                        setTopCustomerSearch={setTopCustomerSearch}
                    />
                ) : (
                    <DetailedInsightsContent
                        datePeriod={datePeriod}
                        setDatePeriod={setDatePeriod}
                        datePresets={dateOptions}
                        searchQuery={detailedApi.searchQuery}
                        setSearchQuery={detailedApi.setSearchQuery}
                        customerOptions={detailedApi.customerOptions}
                        searchLoading={detailedApi.searchLoading}
                        selectedCustomer={detailedApi.selectedCustomer}
                        setSelectedCustomer={detailedApi.setSelectedCustomer}
                        summary={detailedApi.summary}
                        dineIn={detailedApi.dineIn}
                        offPrem={detailedApi.offPrem}
                        orderHistory={detailedApi.orderHistory}
                        latestOrder={detailedApi.latestOrder}
                        favouriteItems={detailedApi.favouriteItems}
                        detailLoading={detailedApi.detailLoading}
                        detailError={detailedApi.detailError}
                        clearCustomer={detailedApi.clearCustomer}
                        orderPage={orderHistoryPage}
                        setOrderPage={setOrderHistoryPage}
                        locationId={detailedApi.locationId}
                    />
                )}
                <div className="cr-mobile-layout__spacer" />
            </div>
            <BottomNav />
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   DESKTOP LAYOUT
   ═══════════════════════════════════════════════════════════════════════════ */
function CustomersReportDesktopContent({ activeTab, setActiveTab, datePeriod, setDatePeriod, orderHistoryPage, setOrderHistoryPage, orderHistorySize, summaryApi, detailedApi, topCustomers, topCustomersLoading, topCustomerSearch, setTopCustomerSearch }: any) {
    const setTopBarProps = useDesktopTopBar();
    // Customers has no live-day data path, so it offers every preset except Today (excludeToday).
    const datePresets = DATE_PRESETS_DESKTOP_NO_TODAY;

    useEffect(() => {
        setTopBarProps({
            breadcrumbs: "Reports",
            title: "Customers",
            onExport: () => {},
            dateFilters: { value: datePeriod, onChange: setDatePeriod, options: datePresets, excludeToday: true },
        });
    }, [setTopBarProps, datePeriod, setDatePeriod]);

    return (
        <main className="cr-desktop-main">
                    {activeTab === "dashboard" ? (
                        <CustomersDashboardContent
                            locationId={summaryApi.locationId}
                            volumeSummary={summaryApi.volumeSummary}
                            byTenure={summaryApi.byTenure}
                            byTotalSpend={summaryApi.byTotalSpend}
                            byAvgCoverSize={summaryApi.byAvgCoverSize}
                            loyaltyCounts={summaryApi.loyaltyCounts}
                            loading={summaryApi.loading}
                            error={summaryApi.error}
                            datePeriod={datePeriod}
                            setDatePeriod={setDatePeriod}
                            datePresets={datePresets}
                            topCustomers={topCustomers}
                            topCustomersLoading={topCustomersLoading}
                            topCustomerSearch={topCustomerSearch}
                            setTopCustomerSearch={setTopCustomerSearch}
                        />
                    ) : (
                        <DetailedInsightsContent
                            datePeriod={datePeriod}
                            setDatePeriod={setDatePeriod}
                            datePresets={datePresets}
                            searchQuery={detailedApi.searchQuery}
                            setSearchQuery={detailedApi.setSearchQuery}
                            customerOptions={detailedApi.customerOptions}
                            searchLoading={detailedApi.searchLoading}
                            selectedCustomer={detailedApi.selectedCustomer}
                            setSelectedCustomer={detailedApi.setSelectedCustomer}
                            summary={detailedApi.summary}
                            dineIn={detailedApi.dineIn}
                            offPrem={detailedApi.offPrem}
                            orderHistory={detailedApi.orderHistory}
                            latestOrder={detailedApi.latestOrder}
                            favouriteItems={detailedApi.favouriteItems}
                            detailLoading={detailedApi.detailLoading}
                            detailError={detailedApi.detailError}
                            clearCustomer={detailedApi.clearCustomer}
                            orderPage={orderHistoryPage}
                            setOrderPage={setOrderHistoryPage}
                            locationId={detailedApi.locationId}
                        />
                    )}
                </main>
    );
}

function CustomersDesktopView({ activeTab, setActiveTab, datePeriod, setDatePeriod, orderHistoryPage, setOrderHistoryPage, orderHistorySize, summaryApi, detailedApi, topCustomers, topCustomersLoading, topCustomerSearch, setTopCustomerSearch }: any) {
    return (
        <DesktopLayout>
            <CustomersReportDesktopContent
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                datePeriod={datePeriod}
                setDatePeriod={setDatePeriod}
                orderHistoryPage={orderHistoryPage}
                setOrderHistoryPage={setOrderHistoryPage}
                orderHistorySize={orderHistorySize}
                summaryApi={summaryApi}
                detailedApi={detailedApi}
                topCustomers={topCustomers}
                topCustomersLoading={topCustomersLoading}
                topCustomerSearch={topCustomerSearch}
                setTopCustomerSearch={setTopCustomerSearch}
            />
        </DesktopLayout>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   ENTRY POINT — single responsive component
   ═══════════════════════════════════════════════════════════════════════════ */
export default function CustomersReport() {
    const isDesktop = useIsDesktop();
    useSwipeBack();
    const navigate = useNavigate();
    const { selectedBranch } = useBranch();
    const { auth, logout } = useAuth();
    const branchName = selectedBranch?.name ?? selectedBranch?.branchName ?? "Select location";
    const displayName = auth?.fullName ?? auth?.userId ?? auth?.userName ?? "User";
    const userRole = auth?.role ?? auth?.designation ?? "Restaurant_Manager";
    const user = {
        initials: getInitials(displayName),
        name: displayName,
        role: userRole,
        onLogout: () => { logout(); navigate("/login", { replace: true }); },
    };

    const [activeTab, setActiveTab] = useState("dashboard");
    // `This Year` is deliberately NOT in `excludeTokens` any more: `getCustomerDateRange` now
    // resolves `thisyear` through the shared `getThisYearRange`, so the pill — and a This Year
    // carried in from another report — both fetch the range the control displays. Re-adding it
    // here would silently drop the user back to Yesterday on a period this screen can serve.
    //
    // The three long-range EOD presets stay listed, and for the reason that once applied to
    // This Year too (DPFIX-11): Customers never OFFERS them, but EOD does and the shared
    // cross-report filter RETAINS any resolvable token (GLOBAL-DP-5), so they arrive by carry —
    // and `getCustomerDateRange` still has no branch for any of them, falling through to its
    // { today, today } default. Falling back to Yesterday loses the range, which is visible;
    // leaving them through kept a multi-month label sitting over one day of data, which is not.
    //
    // The invariant to preserve: every token Customers CAN serve is absent from this list, and
    // every token it cannot is present. Adding a branch to `getCustomerDateRange` means removing
    // its token from here in the same change.
    const [datePeriod, setDatePeriod] = useSharedReportDateFilter("Yesterday", {
        excludeToday: true,
        excludeTokens: ['Last 3 Months', 'Last 6 Months', 'Last Year'],
    });
    const [orderHistoryPage, setOrderHistoryPage] = useState(1);
    const orderHistorySize = 10;
    const [topCustomers, setTopCustomers] = useState([]);
    const [topCustomersLoading, setTopCustomersLoading] = useState(false);
    const [topCustomerSearch, setTopCustomerSearch] = useState("");

    const summaryApi = useCustomerSummaryInsights(datePeriod);
    const detailedApi = useCustomerDetailedInsights(datePeriod, orderHistoryPage, orderHistorySize);

    const locationId = summaryApi.locationId;
    const token = auth?.accessToken ?? auth?.access_token ?? auth?.token ?? null;

    useEffect(() => {
        if (!locationId || !token) {
            setTopCustomers([]);
            return;
        }
        const { startDate, endDate } = getCustomerDateRange(datePeriod);
        setTopCustomersLoading(true);
        fetchTopCustomers({ locationId, startDate, endDate, token, page: 1, size: 50 })
            .then((res) => setTopCustomers(res?.content ?? []))
            .catch(() => setTopCustomers([]))
            .finally(() => setTopCustomersLoading(false));
    }, [locationId, datePeriod, token]);

    if (isDesktop) {
        return (
            <CustomersDesktopView
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                datePeriod={datePeriod}
                setDatePeriod={setDatePeriod}
                orderHistoryPage={orderHistoryPage}
                setOrderHistoryPage={setOrderHistoryPage}
                orderHistorySize={orderHistorySize}
                summaryApi={summaryApi}
                detailedApi={detailedApi}
                topCustomers={topCustomers}
                topCustomersLoading={topCustomersLoading}
                topCustomerSearch={topCustomerSearch}
                setTopCustomerSearch={setTopCustomerSearch}
            />
        );
    }
    return (
        <MobileLayout
            branchName={branchName}
            user={user}
            onBack={() => navigate(-1)}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            datePeriod={datePeriod}
            setDatePeriod={setDatePeriod}
            orderHistoryPage={orderHistoryPage}
            setOrderHistoryPage={setOrderHistoryPage}
            orderHistorySize={orderHistorySize}
            summaryApi={summaryApi}
            detailedApi={detailedApi}
            topCustomers={topCustomers}
            topCustomersLoading={topCustomersLoading}
            topCustomerSearch={topCustomerSearch}
            setTopCustomerSearch={setTopCustomerSearch}
        />
    );
}
